import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DataTable, Select, Typography, useSelect } from '@chilecompra/react-kit/components'
import { onProvidersTypeThunk } from './ProvidersType.actions'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount, formatPercentage } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import ProvidersSeparators from './ProvidersSeparators.container'
import useProvidersChart from '../../components/Charts/ProvidersCharts.hooks'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import ProvidersChart from '../../components/Charts/ProvidersChart'
import ProvidersTypeNoInfo from '../../components/Errors/ProvidersTypeNoInfo'
import FichaProvidersType from '../../components/Infographics/FichaProvidersType'
import { Wrapper, GridWrapper, Exportable, Link } from './ProvidersType.styles'

/**
 * The ProvidersType's container.
 */
const ProvidersType = () => {
  const dispatch = useDispatch()
  const { latestUpdate } = useSelector(state => state.topNavbar)
  const { idEntity, name } = useSelector(state => state.buyerSection)
  const { endYear, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const dataTableRef = useRef()
  const { chartStructure, csvStructure, fetching } = useSelector(state => state.providersType)
  const { fetching: loading, serie: ChartData } = useProvidersChart(chartStructure)
  const [distType, setDistType] = useState('montoBrutoCLPAnualTP')
  const [expandArrow, setExpandArrow] = useState(false)
  const [showTable, setShowTable] = useState(false)
  const [fileName, setFileName] = useState(`Tipos_de_proveedores_${years[0].value}`)
  const { value: yearsValue, onChange: handleChange } = useSelect({
    initialValue: years[0].value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(onProvidersTypeThunk(idEntity, updatedValue))
      setFileName(`Tipos_de_proveedorees_${updatedValue}`)
    }
  })
  const { value: distValue, onChange: handleDistribution } = useSelect({
    initialValue: 'montoBrutoCLPAnualTP',
    changeCallback: updatedValue => setDistType(updatedValue)
  })
  const distribution = [
    { name: 'Monto transado', value: 'montoBrutoCLPAnualTP' },
    { name: 'N° Ordenes de compra', value: 'nroOCAnualTP' },
    { name: 'Monto porcentual', value: 'percentage' }
  ]

  const handleShowTable = updatedValue => setShowTable(updatedValue)
  const handleCaptureImage = () => exportToImage('ProvidersType', fileName)
  const handleDownloadFile = () => exportToCsv(csvStructure, fileName)

  useEffect(() => {
    if (!idEntity || initialLoading) {
      return
    }

    dispatch(onProvidersTypeThunk(idEntity, yearsValue))
  }, [initialLoading, idEntity])

  return (
    <Wrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Tipos de proveedores según su tamaño de empresa
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            Conoce cómo se distribuyen las compras realizadas por {name} según el tamaño de empresa proveedores: micro,
            pequeña, mediana o gran empresa.
            <br />
            <br />A continuación podrás revisar la información sobre montos transados y número de órdenes de compra de
            acuerdo con el tamaño de empresa. Recuerda que estos montos que se extraen de la orden de compra que emite
            una entidad en <Link href="https://www.mercadopublico.cl/Home">www.mercadopublico.cl</Link>, corresponden a
            compromisos de los organismos, no a pagos.
            <br />
            <br />
            La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
            impuestos).
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container alignItems="center" justifyContent="center">
        <GridWrapper container item align="left" md={8} spacing={1} xs={12}>
          <GridWrapper item align="left" md={3} xs={12}>
            <Select label="AÑO" options={years} value={yearsValue} onChange={handleChange} />
          </GridWrapper>
          <GridWrapper item align="left" md={5} xs={12}>
            <Select label="DISTRIBUCIÓN" options={distribution} value={distValue} onChange={handleDistribution} />
          </GridWrapper>
        </GridWrapper>
        <GridWrapper item align="right" md={2} xs={12} margin="6px 0 0">
          <IconButton
            type="toggleChart"
            variant="outlined"
            singleIcon
            margin="24px 10px 0 0"
            disabled={!showTable}
            onClick={() => handleShowTable(false)}
          />
          <IconButton
            type="toggleTable"
            variant="outlined"
            singleIcon
            disabled={showTable}
            margin="24px 0 0"
            onClick={() => handleShowTable(true)}
          />
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10}>
          {showTable ? (
            <DataTable
              headers={[
                { label: 'Año', value: 'year' },
                { label: 'Tamaño proveedor', value: 'ProvidersCat' },
                { label: 'Monto bruto anual', value: 'totalAmount' },
                { label: 'N° de órdenes de compra', value: 'totalOC' },
                { label: 'Monto porcentual', value: 'percentage' }
              ]}
              headersCellsProps={[
                null,
                { enableSort: true },
                { enableSort: true },
                { enableSort: true },
                { enableSort: true }
              ]}
              initialRows={6}
              loading={loading}
              records={ChartData}
              recordsDecorators={[
                ({ year }) => year,
                ({ ProvidersCat }) => (ProvidersCat === 'NoClasificado' ? 'Sin Clasificación' : ProvidersCat),
                ({ totalAmount }) => `$${formatAmount(totalAmount)}`,
                ({ totalOC }) => totalOC,
                ({ percentage }) => formatPercentage(percentage)
              ]}
              ref={dataTableRef}
            />
          ) : ChartData.length >= 1 ? (
            <Exportable id="ProvidersType">
              <ProvidersChart ChartData={ChartData} fetching={fetching} distType={distType} />
              <ProvidersSeparators data={ChartData} />
            </Exportable>
          ) : (
            <GridWrapper item xs={12}>
              <ProvidersTypeNoInfo />
            </GridWrapper>
          )}
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10} margin="32px 0 0">
          <Infobar
            data={csvStructure}
            disableDownloadChart={showTable}
            disableDownloadCsv={!showTable}
            disableInfographic={showTable}
            expandArrow={expandArrow}
            infographic={<FichaProvidersType />}
            latestUpdate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

export default ProvidersType
